import React, { useState, useCallback, memo } from "react";
import { useTranslation } from "react-i18next"; // I18N
import "./YouTubeVideoSection.css";

/**
 * Extracts YouTube video ID from various YouTube URL formats
 */
const extractYouTubeId = (url) => {
  if (!url) return null;
  if (/^[a-zA-Z0-9_-]{11}$/.test(url)) return url;

  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
  );
  return match ? match[1] : null;
};

/**
 * YouTubeVideoSection - Lightweight, fast-loading video component
 */
const YouTubeVideoSection = memo(
  ({ videoUrl, title, description, showTitle = true, className = "" }) => {
    const { t } = useTranslation(); // I18N
    const resolvedTitle = title ?? t("home.video.defaultTitle");
    const [isPlaying, setIsPlaying] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);

    const videoId = extractYouTubeId(videoUrl);

    const handlePlay = useCallback(() => {
      setIsPlaying(true);
    }, []);

    const handleImageLoad = useCallback(() => {
      setImageLoaded(true);
    }, []);

    if (!videoId) return null;

    // Use hqdefault for faster loading (smaller file size than maxresdefault)
    const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

    return (
      <div className={`youtube-video-section ${className}`}>
        {showTitle && (
          <div className="video-section-header">
            <h3 className="video-section-title">{resolvedTitle}</h3>
            {description && (
              <p className="video-section-description">{description}</p>
            )}
          </div>
        )}

        <div className="video-container">
          {!isPlaying ? (
            <div className="video-thumbnail-wrapper" onClick={handlePlay}>
              {/* Skeleton loader */}
              {!imageLoaded && <div className="video-skeleton" />}
              <img
                src={thumbnailUrl}
                alt={resolvedTitle}
                className={`video-thumbnail ${imageLoaded ? "loaded" : ""}`}
                loading="eager"
                onLoad={handleImageLoad}
              />
              <div className="video-overlay">
                <button
                  className="play-button"
                  aria-label={t("home.video.playLabel")}
                  type="button"
                >
                  <svg viewBox="0 0 68 48" className="play-icon">
                    <path
                      className="play-icon-bg"
                      d="M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z"
                    />
                    <path className="play-icon-arrow" d="M 45,24 27,14 27,34" />
                  </svg>
                </button>
              </div>
            </div>
          ) : (
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
              title={resolvedTitle}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="video-iframe"
            />
          )}
        </div>
      </div>
    );
  },
);

YouTubeVideoSection.displayName = "YouTubeVideoSection";

export default YouTubeVideoSection;
